import { useState } from "react";
import { Mail, MapPin, Phone, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import api, { formatApiError } from "../lib/api";
import { useContent } from "../context/ContentContext";
import HeroBackdrop from "../components/HeroBackdrop";

const empty = { name: "", email: "", phone: "", subject: "", message: "", pathway: "" };

export default function Contact() {
  const { t } = useContent();
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thanks! We've received your enquiry and emailed you a confirmation.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden bg-[#0F2744] py-16 lg:py-20">
        <HeroBackdrop sectionKey="contact" image={t("img.contact", "https://images.pexels.com/photos/37832995/pexels-photo-37832995.jpeg?auto=format&fit=crop&q=85&w=1600")} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="eyebrow">Get in touch</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold text-white">{t("contact.title", "Contact us")}</h1>
          <p className="mt-4 text-lg text-slate-200 max-w-2xl">{t("contact.subtitle", "Questions about training, membership or events? We'd love to hear from you.")}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-3 gap-8">
        <div className="space-y-4">
          {[
            { icon: Mail, label: "Email", value: "enquiries@rospabikers.club", href: "mailto:enquiries@rospabikers.club" },
            { icon: Phone, label: "Prefer to call?", value: "Leave your number and we'll ring you", href: null },
            { icon: MapPin, label: "Where we are", value: "West Midlands, United Kingdom", href: null },
          ].map((c, i) => (
            <div key={i} className="rounded-2xl bg-white border border-slate-200 p-6 flex items-start gap-4" data-testid={`contact-info-${i}`}>
              <div className="h-11 w-11 rounded-xl bg-amber-100 flex items-center justify-center shrink-0"><c.icon className="h-6 w-6 text-amber-600" /></div>
              <div>
                <div className="text-sm text-slate-500">{c.label}</div>
                {c.href ? <a href={c.href} className="font-semibold text-[#0F2744] break-all">{c.value}</a> : <div className="font-semibold text-[#0F2744]">{c.value}</div>}
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="mb-1.5 block">Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" />
            </div>
            <div>
              <Label className="mb-1.5 block">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" />
            </div>
            <div>
              <Label className="mb-1.5 block">Phone</Label>
              <Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" />
            </div>
            <div>
              <Label className="mb-1.5 block">Subject *</Label>
              <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-subject" />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Message *</Label>
            <Textarea rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" />
          </div>
          <Button type="submit" disabled={sending} className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold" data-testid="contact-submit">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send message</>}
          </Button>
        </form>
      </div>
    </div>
  );
}
